// The zod side of the form rules. The predicates stay in configRules.ts, the pure
// mirrors of the Go save checks, and each builder here pairs one with the i18n
// key the field shows when it fails. FormMessage translates the key, so a
// schema never calls t() and can be built outside a component.
//
// Every rule except required passes an empty value, as FormKit's did: an
// optional URL field left blank is valid, and a required one says "required"
// rather than "not a URL".
import { z } from "zod";

import type { TranslationKey } from "@/i18n/types";
import {
  isBareHostname,
  isDigitalOceanPorts,
  isEnvVarName,
  isGcpServiceAccountKey,
  isHostOnlyHttpUrl,
} from "@/utils/configRules";
import type { DurationUnit } from "@/utils/durationUnits";
import { isSupportedGelfUrl } from "@/utils/gelfUrl";
import { validUserIdpDisplayName } from "@/utils/userIdpDisplayName";

export const REQUIRED: TranslationKey = "err.fieldRequired";

// A zod message is one string, so a message with values travels as
// "key|{json}" and translateFormMessage splits it back apart.
export const withParams = (key: TranslationKey, params: Record<string, string | number>) =>
  `${key}|${JSON.stringify(params)}`;

// What FormMessage shows for a schema's message: a key (with any values) is
// translated, and anything else is shown as written.
export function translateFormMessage(
  message: string,
  t: (key: string, params?: Record<string, unknown>) => string,
  isKey: (key: string) => boolean,
): string {
  const bar = message.indexOf("|");
  const key = bar === -1 ? message : message.slice(0, bar);
  if (!isKey(key)) return message;
  if (bar === -1) return t(key);
  try {
    return t(key, JSON.parse(message.slice(bar + 1)));
  } catch {
    return t(key);
  }
}

const isEmpty = (value: unknown) => value === undefined || value === null || value === "";

// A text field. Required trims before it checks, so a field of spaces is empty.
// An optional one accepts undefined and null as well, which is what a key the
// API left out or cleared arrives as.
export function text(options: { required?: boolean; message?: TranslationKey } = {}) {
  const message = options.message ?? REQUIRED;
  if (!options.required) return z.string().nullish();
  return z.string({ required_error: message, invalid_type_error: message }).refine((v) => v.trim() !== "", message);
}

// A rule over a text field that ignores an empty value, so it composes with
// either text() shape.
function ruled(test: (value: string) => boolean, message: TranslationKey, required: boolean | undefined) {
  return text({ required }).refine((v) => isEmpty(v) || test(v as string), message);
}

type RuleOptions = { required?: boolean };

// Any http or https URL, path and all. The discovery hosts are probed at a path
// of the server's choosing, so unlike hostOnlyHttpUrl a path is allowed.
export function isHttpUrl(value: unknown): boolean {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

// A GELF listener the server can dial (utils/gelfUrl.ts mirrors the Go list).
export const gelfUrl = (message: TranslationKey) => ruled(isSupportedGelfUrl, message, false);

export const httpUrl = (message: TranslationKey, options: RuleOptions = {}) =>
  ruled(isHttpUrl, message, options.required);

// A whole number, as NumberField holds it (undefined while the box is empty).
export function integer(options: { required?: boolean; min?: number; max?: number } = {}) {
  let schema = z.number({ required_error: REQUIRED, invalid_type_error: REQUIRED }).int("err.wholeNumber");
  if (options.min !== undefined)
    schema = schema.min(options.min, withParams("err.numberAtLeast", { min: options.min }));
  if (options.max !== undefined) schema = schema.max(options.max, withParams("err.numberAtMost", { max: options.max }));
  return options.required ? schema : schema.optional();
}

// A length of time as DurationField holds it: a whole number of `unit`, or
// undefined while empty. The bounds are in `unit` too; the message carries the
// bound and its unit, and DurationField words it in the unit on screen ("Must be
// 28 days or less" while the box shows days).
export function duration(options: { unit: DurationUnit; required?: boolean; min?: number; max?: number }) {
  let schema = z.number({ required_error: REQUIRED, invalid_type_error: REQUIRED }).int("err.wholeNumber");
  if (options.min !== undefined) schema = schema.min(options.min, durationBound("min", options.min, options.unit));
  if (options.max !== undefined) schema = schema.max(options.max, durationBound("max", options.max, options.unit));
  return options.required ? schema : schema.optional();
}

// A duration bound's message: the bound in the stored unit, for the field to
// word in whichever unit it is showing. Read back by parseDurationBound.
export function durationBound(which: "min" | "max", bound: number, unit: DurationUnit): string {
  return withParams(which === "min" ? "err.durationAtLeast" : "err.durationAtMost", { bound, unit });
}

export function parseDurationBound(
  message: string,
): { which: "min" | "max"; bound: number; unit: DurationUnit } | undefined {
  const bar = message.indexOf("|");
  const key = bar === -1 ? message : message.slice(0, bar);
  if (bar === -1 || (key !== "err.durationAtLeast" && key !== "err.durationAtMost")) return undefined;
  try {
    const params = JSON.parse(message.slice(bar + 1)) as { bound: number; unit: DurationUnit };
    return { which: key === "err.durationAtLeast" ? "min" : "max", bound: params.bound, unit: params.unit };
  } catch {
    return undefined;
  }
}

// hostOnlyHTTPURL: an http(s) URL with no path. The message names the vendor's
// own example, so every caller passes one.
export const hostOnlyHttpUrl = (message: TranslationKey, options: RuleOptions = {}) =>
  ruled(isHostOnlyHttpUrl, message, options.required);

export const bareHostname = (message: TranslationKey, options: RuleOptions = {}) =>
  ruled(isBareHostname, message, options.required);

export const gcpServiceAccountKey = (message: TranslationKey = "err.backendGcpServiceAccountKeyInvalid") =>
  ruled(isGcpServiceAccountKey, message, false);

export const digitalOceanPorts = (
  message: TranslationKey = "err.aclDigitalOceanPortsInvalid",
  options: RuleOptions = {},
) => ruled(isDigitalOceanPorts, message, options.required);

export const envVarName = (message: TranslationKey, options: RuleOptions = {}) =>
  ruled(isEnvVarName, message, options.required);

// The name on an additional identity provider's sign-in button. Required, so
// its own message says which field is missing.
export const userIdpDisplayName = () =>
  text({ required: true, message: "err.userIdpDisplayNameRequired" }).refine(
    validUserIdpDisplayName,
    "err.userIdpDisplayNameInvalid",
  );

// A new password: required, 8 to 64 characters, the range the server enforces.
export const password = () =>
  text({ required: true }).refine((v) => v.length >= 8 && v.length <= 64, "err.passwordLengthRange");

// A cross-field rule for an object schema: the confirmation must equal the
// password. The issue lands on the confirmation field, which is where the admin
// looks. zod runs it only once every field passes, so where the password has a
// rule of its own, prefer checkNewPassword, which says both at once.
export function passwordsMustMatch<T extends Record<string, unknown>>(
  password: keyof T & string,
  confirm: keyof T & string,
) {
  return (value: T, ctx: z.RefinementCtx) => {
    if (!isEmpty(value[confirm]) && value[password] !== value[confirm]) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "err.passwordsDontMatch", path: [confirm] });
    }
  };
}

// A stored credential is never sent back, so on edit the field is empty and an
// empty value keeps what is stored. Required only on create.
export const secret = (options: { recorded: boolean; message?: TranslationKey }) =>
  text({ required: !options.recorded, message: options.message });

const inPasswordRange = (v: string) => v.length >= 8 && v.length <= 64;

// A new password and its confirmation, checked from the object's superRefine
// because the pair applies only to some accounts (a local one, not SAML or
// LDAP), and a hidden field keeps its value: a rule on the field itself would
// block the save of an LDAP account over a password nobody can see. On an edit
// both may stay empty, which keeps the stored password.
export function checkNewPassword(
  value: Record<string, unknown>,
  ctx: z.RefinementCtx,
  options: { required: boolean; password?: string; confirm?: string },
) {
  const passwordKey = options.password ?? "PlainPassword";
  const confirmKey = options.confirm ?? "ConfirmPassword";
  const password = (value[passwordKey] as string | undefined) ?? "";
  const confirm = (value[confirmKey] as string | undefined) ?? "";
  const issue = (path: string, message: TranslationKey) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });

  if (password === "") {
    if (options.required) issue(passwordKey, REQUIRED);
  } else if (!inPasswordRange(password)) {
    issue(passwordKey, "err.passwordLengthRange");
  }
  if (confirm === "") {
    if (options.required) issue(confirmKey, REQUIRED);
  } else if (confirm !== password) {
    issue(confirmKey, "err.passwordsDontMatch");
  }
}
